import { PayrConfigError } from './types.js';
import type { TenancyResponse } from './api/client.js';
import {
  resolveSdkAppearance,
  type PayrAppearanceInput,
  type ResolvedSdkAppearance,
} from './theme/appearance.js';

/**
 * Feature flags for SDK capabilities (Phase 2+)
 */
export interface PayrSDKFeatures {
  apms?: boolean;
  /** Apple Pay on the Web (WorldPay wallet flow; requires HTTPS on the host page). */
  applePay?: boolean;
  /** Google Pay on the Web (Worldpay gateway; requires HTTPS and Google Pay API merchant setup). */
  googlePay?: boolean;
  recurring?: boolean;
}

interface PayrSDKConfigBase {
  /** PAYR API base URL (no trailing slash) */
  apiBaseUrl: string;
  /** Auth token from server-to-server onboarding */
  authToken: string;
  /** DOM selector or element for mounting the payment form */
  container: string | HTMLElement;
  /** Optional override: skip backend fetch (for testing) */
  checkoutId?: string;
  /** Optional override: sandbox or production */
  environment?: 'sandbox' | 'production';
  /** Enable debug mode (verbose logging). Default: false */
  debug?: boolean;
  /** Feature flags (Phase 2+) */
  features?: PayrSDKFeatures;
  /** Success callback */
  onSuccess?: (result: unknown) => void;
  /** Error callback */
  onError?: (error: Error) => void;
  /** Optional theming: border radius, primary color, web-safe font, typography roles. */
  appearance?: PayrAppearanceInput;
  /** Optional payment reference attached to all payments from this session. */
  reference?: string;
}

/**
 * Use when the payment amount is fixed and set at initialization. `pure_amount` is required.
 */
interface PayrSDKConfigWithFixedAmount extends PayrSDKConfigBase {
  isAmountEditable?: false;
  /**
   * Base payment amount in **pounds** (e.g. `10.50`).
   * Required when `isAmountEditable` is not `true`.
   */
  pure_amount: number;
}

/**
 * Use when the end user should be able to enter or update the payment amount in the SDK UI.
 * `pure_amount` is optional and pre-fills the amount input when provided.
 */
interface PayrSDKConfigWithEditableAmount extends PayrSDKConfigBase {
  isAmountEditable: true;
  /**
   * Optional pre-fill value in **pounds** for the amount input (e.g. `10.50`).
   * When omitted the input starts empty.
   */
  pure_amount?: number;
}

/**
 * Configuration for PAYR SDK initialization.
 *
 * - `pure_amount` is **required** when `isAmountEditable` is not `true`.
 * - Set `isAmountEditable: true` to render an editable amount field in the SDK UI.
 */
export type PayrSDKConfig = PayrSDKConfigWithFixedAmount | PayrSDKConfigWithEditableAmount;

/**
 * Resolved configuration with all derived values (built during init, stored internally).
 */
export interface ResolvedPayrSDKConfig {
  apiBaseUrl: string;
  authToken: string;
  container: string | HTMLElement;
  containerElement: HTMLElement;
  /** Resolved tenancy ID (from tenancies API) */
  tenancyId: string;
  /** WorldPay checkout ID (from backend or override) */
  checkoutId: string;
  /** Resolved WorldPay SDK URL */
  sdkUrl: string;
  environment: 'sandbox' | 'production';
  debug?: boolean;
  features?: PayrSDKFeatures;
  onSuccess?: (result: unknown) => void;
  onError?: (error: Error) => void;
  appearance?: PayrAppearanceInput;
  resolvedAppearance: ResolvedSdkAppearance;
  selectedTenancy?: TenancyResponse;
  /** Fee rate resolved from env / default (~1.99%). Internal only. */
  paymentFeeRate: number;
  /** Always 'GBP'. Internal only. */
  currency: string;
  pure_amount?: number;
  isAmountEditable?: boolean;
  reference?: string;
}

/**
 * Validated init config (before backend fetch)
 */
export interface ValidatedPayrSDKConfig {
  apiBaseUrl: string;
  authToken: string;
  container: string | HTMLElement;
  containerElement: HTMLElement;
  checkoutId?: string;
  environment?: 'sandbox' | 'production';
  debug?: boolean;
  features?: PayrSDKFeatures;
  onSuccess?: (result: unknown) => void;
  onError?: (error: Error) => void;
  pure_amount?: number;
  isAmountEditable?: boolean;
  reference?: string;
  appearance?: PayrAppearanceInput;
  resolvedAppearance: ResolvedSdkAppearance;
}

const WORLDPAY_SDK_URLS = {
  sandbox: 'https://try.access.worldpay.com/access-checkout/v2/checkout.js',
  production: 'https://access.worldpay.com/access-checkout/v2/checkout.js',
} as const;

/** Always GBP — currency is not configurable. */
export const DEFAULT_SDK_CURRENCY = 'GBP';

function validatedApiBaseUrl(raw: unknown): string {
  if (!raw || typeof raw !== 'string') {
    throw new PayrConfigError('apiBaseUrl is required and must be a non-empty string');
  }
  const apiBaseUrl = raw.trim().replace(/\/$/, '');
  if (!apiBaseUrl) {
    throw new PayrConfigError('apiBaseUrl cannot be empty');
  }
  try {
    new URL(apiBaseUrl);
  } catch {
    throw new PayrConfigError('apiBaseUrl must be a valid URL');
  }
  return apiBaseUrl;
}

function validatedAuthToken(raw: unknown): string {
  if (!raw || typeof raw !== 'string') {
    throw new PayrConfigError('authToken is required and must be a non-empty string');
  }
  const authToken = raw.trim();
  if (!authToken) {
    throw new PayrConfigError('authToken cannot be empty');
  }
  return authToken;
}

function validatedOptionalFn<T extends (...args: never[]) => unknown>(
  raw: unknown,
  name: string
): T | undefined {
  if (raw == null) return undefined;
  if (typeof raw !== 'function') {
    throw new PayrConfigError(`${name} must be a function when provided`);
  }
  return raw as T;
}

function resolveContainerElement(container: PayrSDKConfig['container']): HTMLElement {
  if (!container) {
    throw new PayrConfigError('container is required');
  }
  if (typeof container === 'string') {
    const el = document.querySelector(container);
    if (!el || !(el instanceof HTMLElement)) {
      throw new PayrConfigError(`container selector "${container}" did not match an HTMLElement`);
    }
    return el;
  }
  if (container instanceof HTMLElement) {
    return container;
  }
  throw new PayrConfigError('container must be a CSS selector string or an HTMLElement');
}

function createValidatedConfigCore(config: PayrSDKConfig): ValidatedPayrSDKConfig {
  return {
    apiBaseUrl: validatedApiBaseUrl(config.apiBaseUrl),
    authToken: validatedAuthToken(config.authToken),
    container: config.container,
    containerElement: resolveContainerElement(config.container),
    debug: config.debug,
    features: config.features,
    onSuccess: validatedOptionalFn<(result: unknown) => void>(config.onSuccess, 'onSuccess'),
    onError: validatedOptionalFn<(error: Error) => void>(config.onError, 'onError'),
    resolvedAppearance: resolveSdkAppearance(config.appearance),
  };
}

function assignTrimmedCheckoutId(result: ValidatedPayrSDKConfig, raw: string | undefined): void {
  if (raw == null || raw === '') return;
  const checkoutId = raw.trim();
  if (checkoutId) result.checkoutId = checkoutId;
}

function assignValidatedPureAmount(
  result: ValidatedPayrSDKConfig,
  pureAmount: number | undefined
): void {
  if (pureAmount === undefined) return;
  if (typeof pureAmount !== 'number' || !Number.isFinite(pureAmount) || pureAmount <= 0) {
    throw new PayrConfigError('pure_amount must be a finite number greater than 0 when provided');
  }
  result.pure_amount = pureAmount;
}

function assignOptionalReference(result: ValidatedPayrSDKConfig, raw: unknown): void {
  if (raw == null) return;
  if (typeof raw !== 'string') {
    throw new PayrConfigError('reference must be a string');
  }
  const ref = raw.trim();
  if (ref) result.reference = ref;
}

function assignOptionalInitFields(result: ValidatedPayrSDKConfig, config: PayrSDKConfig): void {
  assignTrimmedCheckoutId(result, config.checkoutId);
  if (config.environment != null) {
    if (config.environment !== 'sandbox' && config.environment !== 'production') {
      throw new PayrConfigError('environment must be either "sandbox" or "production"');
    }
    result.environment = config.environment;
  }
  assignValidatedPureAmount(result, config.pure_amount);
  if (
    Object.prototype.hasOwnProperty.call(config, 'isAmountEditable') &&
    config.isAmountEditable != null
  ) {
    if (typeof config.isAmountEditable !== 'boolean') {
      throw new PayrConfigError('isAmountEditable must be a boolean when provided');
    }
    if (config.isAmountEditable === true) {
      result.isAmountEditable = true;
    }
  }
  assignOptionalReference(result, config.reference);
}

/**
 * Validate and resolve configuration (before backend fetch).
 *
 * Throws `PayrConfigError` if required fields are missing or invalid.
 * `pure_amount` is required unless `isAmountEditable: true`.
 */
export function validateConfig(config: PayrSDKConfig): ValidatedPayrSDKConfig {
  if (config == null || typeof config !== 'object') {
    throw new PayrConfigError('config is required');
  }

  const result = createValidatedConfigCore(config);
  assignOptionalInitFields(result, config);
  result.appearance = config.appearance;

  if (result.isAmountEditable !== true && result.pure_amount == null) {
    throw new PayrConfigError(
      'pure_amount is required when isAmountEditable is not enabled. Provide init.pure_amount or set isAmountEditable: true.'
    );
  }

  return result;
}

/**
 * Google Pay JS `PaymentsClient` environment from PAYR SDK `environment`.
 * **`sandbox`** → **`TEST`**; **`production`** or omitted (after checkout resolve) → **`PRODUCTION`**.
 */
export function resolveGooglePayJsEnvironment(
  sdkEnvironment: 'sandbox' | 'production' | undefined
): 'TEST' | 'PRODUCTION' {
  return sdkEnvironment === 'sandbox' ? 'TEST' : 'PRODUCTION';
}

/**
 * Get WorldPay SDK URL for given environment
 */
export function getWorldPaySdkUrl(environment: 'sandbox' | 'production' = 'production'): string {
  return WORLDPAY_SDK_URLS[environment];
}

export { WORLDPAY_SDK_URLS };
